/**
 * Sector maps: choose what you want, get a PDF.
 *
 * This page used to be a preview of a sheet of paper that the browser then
 * printed. Two engines disagreed about that sheet often enough — Safari split
 * the figure from its caption, printed three columns as one, and ignores a
 * per-page orientation in a job that mixes them — that the fixes had become a
 * list of workarounds rather than a design. Steward writes the document itself
 * now, so the page box, the orientation and the page breaks are decisions
 * instead of outcomes. See plat/document.ts.
 *
 * The map on screen is a preview of one sheet. Everything else here is the
 * order form.
 *
 * A sheet has two axes. Which ground it covers is the sector, chosen here.
 * *Who it is about* is normally everyone living on that ground — but it can be
 * narrowed to a saved report or to a search handed over from /search, which
 * arrives in the query string. The sector list, the roster and the PDF are all
 * the same afterwards; there are simply fewer households on them.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  useFilterSearch, useParcels, useReport, useReports, useSectorCatalog,
  type FilterGroup, type SearchHouseholdRow, type SearchPeopleRow, type SearchScope,
} from '../api';
import PlatMap from '../components/PlatMap';
import {
  buildSectorPdf, householdsIn, openBlob, pdfFilename, releaseTab, reserveTab,
  type SheetSpec,
} from '../plat/document';
import { type PlatData, type PlatFocus, type PlatHousehold } from '../plat/model';

/** Matches the key the parcel asset was built with (see refresh_parcels.py). */
const addrKey = (formatted: string | null) =>
  (formatted ?? '').split(/<br\s*\/?>/i)[0].replace(/\s+/g, ' ').trim().toUpperCase();

const WARD_KEY = '__ward__';
const WARD_TITLE = 'Lorraine Ward';

/** No conditions: every household in the ward. */
const EVERYONE: FilterGroup = { op: 'AND', conditions: [] };

/** A sheet, plus what the order form needs to draw its row. */
interface Sheet extends SheetSpec {
  /** A walk inside a sector, rather than a sector or the ward. */
  walk: boolean;
  count: number;
}

export default function Sectors() {
  const { data: catalog } = useSectorCatalog();
  const macros = useMemo(() => catalog?.macros ?? [], [catalog]);

  const parcels = useParcels();
  const plat = parcels.data as PlatData | undefined;

  // One unfiltered query for the whole ward. Every sheet needs every
  // household: a lot can hold two families of different sectors, and a
  // single-sector sheet still has to name both of them on it. A limit narrows
  // who the sheet is about, never what it is drawn from, so this query stays
  // whole whatever is chosen below.
  const search = useFilterSearch('households', EVERYONE, { limit: 500 });
  const rows = (search.data?.results ?? []) as SearchHouseholdRow[];

  const households: PlatHousehold[] = useMemo(
    () => rows.map((r) => ({
      id: r.id,
      key: addrKey(r.formatted_address),
      parcelKey: r.parcel_key,
      unit: r.addr_unit,
      label: r.name_directory ?? '',
      lat: r.lat,
      lng: r.lng,
      oldestAge: r.oldest_age,
      macro: r.sector_macro,
      micro: r.sector_micro,
    })),
    [rows],
  );

  const limit = useLimit();
  const limitTo = limit.ids;

  const [selected, setSelected] = useState<Set<string>>(new Set([WARD_KEY]));
  const [includeWalks, setIncludeWalks] = useState(true);
  const [showShading, setShowShading] = useState(true);
  const [showNames, setShowNames] = useState(true);
  const [showRoster, setShowRoster] = useState(true);
  const [preview, setPreview] = useState(WARD_KEY);
  const [busy, setBusy] = useState<{ done: number; total: number; label: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Sheets in the order they will be bound: the ward, then each sector, then
  // the walks inside it.
  //
  // A limited run hides the sectors it does not reach. A checkbox for a walk
  // with nothing on it is an offer to print a blank page, and a limit of
  // fourteen households leaves most of the ward blank.
  const allSheets: Sheet[] = useMemo(() => {
    const count = (focus: PlatFocus) => householdsIn(households, focus, limitTo).length;
    const keep = (n: number) => !limitTo || n > 0;
    const out: Sheet[] = [{
      key: WARD_KEY, title: WARD_TITLE, focus: { kind: 'ward' },
      walk: false, count: count({ kind: 'ward' }),
    }];
    for (const m of macros) {
      const macro: PlatFocus = { kind: 'macro', name: m.name };
      if (!keep(count(macro))) continue;
      out.push({ key: m.name, title: m.name, focus: macro,
                 walk: false, count: count(macro) });
      for (const mi of m.micros) {
        const micro: PlatFocus = { kind: 'micro', name: mi };
        if (!keep(count(micro))) continue;
        out.push({ key: mi, title: mi, focus: micro, walk: true, count: count(micro) });
      }
    }
    return out;
  }, [macros, households, limitTo]);

  /** The sectors, each with its walks, in the order the list draws them. */
  const tree = useMemo(() => allSheets
    .filter((s) => s.key !== WARD_KEY && !s.walk)
    .map((m) => ({
      ...m,
      micros: allSheets.filter(
        (w) => w.walk
          && (macros.find((x) => x.name === m.key)?.micros ?? []).includes(w.key)),
    })),
    [allSheets, macros]);

  const wardSheet = allSheets[0];

  const chosen = useMemo(
    () => allSheets.filter((s) => selected.has(s.key)),
    [allSheets, selected],
  );

  const previewSheet = useMemo(
    () => allSheets.find((s) => s.key === preview) ?? allSheets[0],
    [allSheets, preview],
  );

  const setMacro = (name: string, on: boolean) => {
    const micros = tree.find((m) => m.key === name)?.micros.map((w) => w.key) ?? [];
    setSelected((prev) => {
      const next = new Set(prev);
      const apply = (k: string) => (on ? next.add(k) : next.delete(k));
      apply(name);
      if (includeWalks) micros.forEach(apply);
      return next;
    });
  };

  const setMicro = (name: string, on: boolean) => setSelected((prev) => {
    const next = new Set(prev);
    if (on) next.add(name); else next.delete(name);
    return next;
  });

  // The walks toggle is an action on the selection rather than a filter over
  // it: checking it adds every walk of every sector already chosen, clearing
  // it takes them away. Which is the common case — a sector on its own, or a
  // sector followed by its walks.
  const applyWalks = (on: boolean) => {
    setIncludeWalks(on);
    setSelected((prev) => {
      const next = new Set(prev);
      for (const m of tree) {
        if (!prev.has(m.key)) continue;
        for (const w of m.micros) { if (on) next.add(w.key); else next.delete(w.key); }
      }
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(
    allSheets.filter((s) => includeWalks || !s.walk).map((s) => s.key)));

  const generate = async () => {
    if (!plat || !chosen.length) return;
    // Claimed on the click, before the first await: a tab asked for after the
    // sheets have been drawn is a popup as far as the browser is concerned.
    const tab = reserveTab();
    setError(null);
    setBusy({ done: 0, total: chosen.length, label: chosen[0].title });
    try {
      const blob = await buildSectorPdf({
        plat,
        households,
        sheets: chosen,
        limitTo,
        limitName: limit.name ?? undefined,
        flags: { showShading, showNames, showRoster },
        onProgress: (done, total, label) => setBusy({ done, total, label }),
      });
      openBlob(blob, pdfFilename(), tab);
    } catch (e) {
      releaseTab(tab);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  // Keep the preview on something that is actually in the document.
  useEffect(() => {
    if (chosen.length && !chosen.some((s) => s.key === preview)) {
      setPreview(chosen[0].key);
    }
  }, [chosen, preview]);

  // A limit can take sectors off the list. Drop them from the selection too,
  // or the count on the Generate button promises pages that are not offered.
  useEffect(() => {
    setSelected((prev) => {
      const keys = new Set(allSheets.map((s) => s.key));
      const kept = [...prev].filter((k) => keys.has(k));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [allSheets]);

  const loading = search.isLoading || parcels.isLoading || limit.loading;

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4">
      <LimitBar limit={limit} households={wardSheet?.count ?? 0} />

      <div className="grid gap-4 md:grid-cols-[1fr_16rem]">
        <section className="rounded border border-slate-200 dark:border-slate-700">
          <header className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
            <h2 className="text-sm font-semibold">Sectors to include</h2>
            <span className="flex gap-2 text-xs">
              <button onClick={selectAll} className="text-sky-700 hover:underline dark:text-sky-400">
                All
              </button>
              <button onClick={() => setSelected(new Set())}
                      className="text-slate-500 hover:underline">
                None
              </button>
            </span>
          </header>

          <div className="max-h-[22rem] overflow-y-auto p-3 text-sm">
            <Row
              label={`${WARD_TITLE} — every sector`}
              count={wardSheet?.count ?? 0}
              on={selected.has(WARD_KEY)}
              set={(v) => setMicro(WARD_KEY, v)}
              onPreview={() => setPreview(WARD_KEY)}
              active={preview === WARD_KEY}
            />
            {tree.map((m) => (
              <div key={m.key} className="mt-1">
                <Row
                  label={m.title}
                  count={m.count}
                  on={selected.has(m.key)}
                  set={(v) => setMacro(m.key, v)}
                  onPreview={() => setPreview(m.key)}
                  active={preview === m.key}
                />
                {includeWalks && m.micros.map((w) => (
                  <div key={w.key} className="pl-6">
                    <Row
                      label={w.title}
                      count={w.count}
                      on={selected.has(w.key)}
                      set={(v) => setMicro(w.key, v)}
                      onPreview={() => setPreview(w.key)}
                      active={preview === w.key}
                      small
                    />
                  </div>
                ))}
              </div>
            ))}
            {limit.ids && !tree.length && !loading && (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                No sector holds any of these households.
              </p>
            )}
          </div>
        </section>

        <section className="space-y-3">
          <div className="rounded border border-slate-200 p-3 dark:border-slate-700">
            <h2 className="mb-2 text-sm font-semibold">On each sheet</h2>
            <div className="space-y-1 text-sm">
              <Check on={includeWalks} set={applyWalks} label="Walk maps" />
              <Check on={showRoster} set={setShowRoster} label="Household list" />
              <hr className="my-2 border-slate-200 dark:border-slate-700" />
              <Check on={showShading} set={setShowShading} label="Shading" />
              <Check on={showNames} set={setShowNames} label="Names" />
            </div>
          </div>

          <button
            onClick={generate}
            disabled={!plat || !chosen.length || !!busy || limit.ids?.size === 0}
            className="w-full rounded border border-slate-800 bg-slate-800 px-3 py-2 text-sm font-medium text-white disabled:opacity-50 dark:border-slate-200 dark:bg-slate-200 dark:text-slate-900"
          >
            {busy
              ? `${busy.done} of ${busy.total}…`
              : `Generate PDF — ${chosen.length} map${chosen.length === 1 ? '' : 's'}`}
          </button>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {busy
              ? busy.label
              : `${chosen.length + (showRoster ? chosen.length : 0)} pages or so, Letter, `
                + 'each map turned whichever way suits it.'}
          </p>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </section>
      </div>

      {parcels.isError && (
        <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          Parcel data is unavailable. Run <code>python3 -m tools.refresh_parcels</code>,
          then deploy.
        </p>
      )}

      {loading && <p className="text-sm text-slate-500">Loading the ward…</p>}

      {/* Nothing is previewed until the limit has resolved: drawing the whole
          ward for a moment and then snapping to fourteen households reads as
          a bug, not as loading. */}
      {plat && previewSheet && !limit.loading && (
        <section className="rounded border border-slate-200 bg-white p-3 dark:border-slate-700">
          <PlatMap
            data={plat}
            households={households}
            focus={previewSheet.focus}
            limitTo={limitTo}
            title={previewSheet.title}
            subtitle={`${previewSheet.count} household${
              previewSheet.count === 1 ? '' : 's'}${
              limit.name ? ` — ${limit.name}` : ''} — preview`}
            showShading={showShading}
            showNames={showNames}
          />
        </section>
      )}
    </div>
  );
}

/** What the sheets are limited to, as the order form needs to see it. */
interface Limit {
  /** Household ids the sheets are about. Undefined means the whole ward. */
  ids?: ReadonlySet<string>;
  /** What it is called, on screen and in the PDF. */
  name: string | null;
  /** The URL asked for a limit — true even while it is still resolving. */
  asked: boolean;
  loading: boolean;
  /** People-scope only: how many people matched. */
  people: number | null;
  /** People-scope only: matched people belonging to no household. */
  homeless: number;
  clear: () => void;
  pick: (reportId: string) => void;
}

/**
 * The limit, read out of the query string.
 *
 * Two ways in, because there are two things worth mapping. A saved report is
 * `?report=<id>` — the same handle /reports and /search already pass around.
 * An unsaved search has no id to pass, so it travels as its own filter tree in
 * `?scope=&filter=`, which is what makes a map shareable and survive a reload.
 *
 * Either way what comes back out is a set of household ids. A people-scope
 * report resolves through `household_id`, so "everyone over 80" maps as the
 * doors you would knock on rather than as one pin per person.
 */
function useLimit(): Limit {
  const [qs, setQs] = useSearchParams();
  const reportId = qs.get('report') ?? '';
  const raw = qs.get('filter') ?? '';
  const asked = !!reportId || !!raw;

  const report = useReport(reportId);

  // Malformed JSON here is a hand-edited URL, not a bug worth blanking the
  // page over: fall back to no limit and let the bar say so.
  const adHoc = useMemo<FilterGroup | null>(() => {
    if (!raw) return null;
    try { return JSON.parse(raw) as FilterGroup; } catch { return null; }
  }, [raw]);

  const scope: SearchScope = reportId
    ? (report.data?.scope ?? 'households')
    : (qs.get('scope') === 'people' ? 'people' : 'households');
  const definition = reportId ? (report.data?.definition ?? null) : adHoc;

  const results = useFilterSearch(scope, definition ?? EVERYONE, {
    // The server's ceiling rather than the hook's default of 500. A
    // people-scope report over the ward runs well past five hundred rows, and
    // a truncated one would quietly leave households off the map.
    limit: 2000,
    enabled: !!definition,
  });

  const rows = results.data?.results;
  const type = results.data?.type;
  const name = reportId ? (report.data?.name ?? null)
                        : (adHoc ? 'Search results' : null);

  return useMemo<Limit>(() => {
    const clear = () => setQs({});
    const pick = (id: string) => setQs({ report: id });
    const base = { name, asked, clear, pick };
    // `rows` can outlive a cleared limit — the query hook keeps the previous
    // page's data — so `asked` is what decides whether there is a limit at all.
    if (!asked || !rows) {
      return { ...base, ids: undefined, loading: asked, people: null, homeless: 0 };
    }
    if (type === 'people') {
      const people = rows as SearchPeopleRow[];
      return {
        ...base,
        ids: new Set(people.map((r) => r.household_id)
                           .filter((v): v is string => !!v)),
        loading: false,
        people: people.length,
        homeless: people.filter((r) => !r.household_id).length,
      };
    }
    return {
      ...base,
      ids: new Set((rows as SearchHouseholdRow[]).map((r) => r.id)),
      loading: false,
      people: null,
      homeless: 0,
    };
  }, [asked, name, rows, type, setQs]);
}

/**
 * What the sheets are about, and how to change it.
 *
 * Deliberately one line when unlimited. The whole ward is the normal case and
 * a banner announcing it would be furniture; the picker sits there quietly for
 * the times it is not.
 */
function LimitBar({ limit, households }: { limit: Limit; households: number }) {
  const { data } = useReports();
  const reports = data?.reports ?? [];
  const plural = (n: number) => `${n} household${n === 1 ? '' : 's'}`;

  if (!limit.asked) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <span>Every household in the ward.</span>
        {reports.length > 0 && (
          <select
            value=""
            onChange={(e) => e.target.value && limit.pick(e.target.value)}
            className="rounded border border-slate-300 px-2 py-0.5 text-xs dark:border-slate-600 dark:bg-slate-900"
          >
            <option value="">Limit to a report…</option>
            {reports.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        )}
        <Link to="/search" className="text-sky-700 hover:underline dark:text-sky-400">
          or build a search →
        </Link>
      </div>
    );
  }

  // People come in as people and go onto the map as doors, so both numbers are
  // worth saying: sixteen people can be nine households, and somebody counting
  // pins against their report should not have to work that out.
  const counted = limit.people != null
    ? `${plural(households)} · ${limit.people} `
      + `${limit.people === 1 ? 'person' : 'people'}`
      + (limit.homeless
        ? `, ${limit.homeless} in no household — not mapped` : '')
    : plural(households);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm dark:border-sky-900 dark:bg-sky-950/40">
      <span className="font-medium">Limited to: {limit.name ?? 'a search'}</span>
      <span className="text-xs text-slate-600 dark:text-slate-300">
        {limit.loading ? 'counting…'
          : households === 0 ? 'nothing matched — no map to draw'
            : counted}
      </span>
      <button
        onClick={limit.clear}
        className="ml-auto text-xs text-slate-500 hover:underline dark:text-slate-400"
      >
        ✕ show the whole ward
      </button>
    </div>
  );
}

function Row({ label, count, on, set, onPreview, active, small }: {
  label: string; count: number; on: boolean; set: (v: boolean) => void;
  onPreview: () => void; active: boolean; small?: boolean;
}) {
  return (
    <div className={`flex items-center gap-2 rounded px-1 ${
      active ? 'bg-slate-100 dark:bg-slate-800' : ''}`}>
      <input type="checkbox" checked={on} onChange={(e) => set(e.target.checked)} />
      <button
        onClick={onPreview}
        className={`flex-1 truncate text-left hover:underline ${
          small ? 'text-xs text-slate-600 dark:text-slate-300' : ''}`}
        title="Preview this sheet"
      >
        {label}
      </button>
      <span className="text-xs tabular-nums text-slate-400">{count}</span>
    </div>
  );
}

function Check({ on, set, label }: {
  on: boolean; set: (v: boolean) => void; label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2">
      <input type="checkbox" checked={on} onChange={(e) => set(e.target.checked)} />
      {label}
    </label>
  );
}
